import { z } from "zod";

import type { MediaItem } from "@/api/admin/branches";
import {
  POS_INTEGRATORS,
  PosIntegrator,
  PosName,
  PosQrPattern,
} from "@/api/admin/posIntegration/types";

export const PosIntegratorList = POS_INTEGRATORS.map(value => ({
  id: value,
  name: value,
}));

export const TableDigitsList = Array.from({ length: 10 }, (_, i) => {
  const value = (i + 1).toString();
  return {
    name: value,
    id: value,
  };
});

export enum ServiceBranchEnum {
  DINE_IN = "Dine-In",
  CASH = "Cash & Carry",
  QSR = "QSR",
  DRIVE = "Drive-thru",
}

export enum TablesBranchEnum {
  QR_WITH = "QR code with table #",
  QW_WITHOUT = "QR code without table #",
  NO_QR = "No QR codes on tables",
}

export enum OperationsBranchEnum {
  AUTO_CLOSE = "Auto-closing orders",
  MANUAL_CLOSE = "Manual closing orders",
}

export const serviceTypeList = Object.values(ServiceBranchEnum).map(value => ({
  id: value,
  name: value,
}));

export const tablesList = Object.values(TablesBranchEnum).map(value => ({
  title: value,
  value: value,
}));

export const operationsList = Object.values(OperationsBranchEnum).map(
  value => ({
    title: value,
    value: value,
  })
);

export const adminBoostSchema = z.object({
  overrideScore: z
    .string()
    .refine(val => !isNaN(Number(val)), { message: "Score must be a number" }),
  automatic: z.boolean(),
  score: z.string().optional(),
});

export type AdminBoostInput = z.input<typeof adminBoostSchema>;

export type AdminBoostType = z.output<typeof adminBoostSchema>;

export const adminSetUpSchema = z.object({
  cashback: z.coerce
    .number({ message: "Cashback must be a number" })
    .int("Number must be integer")
    .min(0, "Cashback must be ≥ 0")
    .max(100, "Cashback must be ≤ 100"),
  serviceTypes: z.array(z.nativeEnum(ServiceBranchEnum)).optional().default([]),
  tables: z.nativeEnum(TablesBranchEnum).optional(),
  operations: z.nativeEnum(OperationsBranchEnum).optional(),
});

export type AdminSetUpType = z.input<typeof adminSetUpSchema>;

export const BranchPosSchema = z
  .object({
    isPosIntegrated: z.boolean().default(false),
    /** Supported providers, or UNKNOWN when the API reports another type. */
    posName: z.enum(PosName).optional(),
    /** When posName is UNKNOWN, the raw type string from the API (or branch). */
    posRawType: z.string().optional(),
    integrator: z.enum(PosIntegrator).optional(),
    qrScannerEnabled: z.boolean().default(false),
    qrPattern: z.enum(PosQrPattern).or(z.literal("")).optional(),
    posClientId: z.string().optional(),
    posBranchId: z.string().optional(),
    posTenderId: z.string().optional(),
    posDiscountId: z.string().optional(),
    baseUrl: z.string().optional(),
    tableNumberLength: z.string().default("3"),
    isSettlementEnabled: z
      .boolean()
      .nullish()
      .transform(v => v ?? false),
    autoClose: z
      .boolean()
      .nullish()
      .transform(v => v ?? false),
    isBillLookupEnabled: z.boolean().default(false),
    isTableNumberLookupEnabled: z.boolean().default(false),
    isTableIdLookupEnabled: z.boolean().default(false),
    tableIdMappingFileName: z.string().nullish(),
    tableIdMappingRemoved: z.boolean().optional(),
    tableIdMappingFile: z.instanceof(File).optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      data.isPosIntegrated &&
      !data.isBillLookupEnabled &&
      !data.isTableNumberLookupEnabled &&
      !data.isTableIdLookupEnabled
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one lookup method must be selected",
        path: ["isBillLookupEnabled"],
      });
    }

    if (data.isPosIntegrated && !data.posName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["posName"],
      });
    }

    if (
      data.isPosIntegrated &&
      data.posName === PosName.UNKNOWN &&
      !data.posRawType
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select Revel or Micros to save changes",
        path: ["posName"],
      });
    }

    if (data.isPosIntegrated && !data.integrator) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["integrator"],
      });
    }

    const hasPendingMapping = data.tableIdMappingFile instanceof File;
    const hasSavedMapping =
      Boolean(data.tableIdMappingFileName) && !data.tableIdMappingRemoved;
    if (data.isTableIdLookupEnabled && !hasPendingMapping && !hasSavedMapping) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Upload a table ID mapping CSV",
        path: ["tableIdMappingFile"],
      });
    }

    if (data.isPosIntegrated) {
      if (!data.posClientId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["posClientId"],
        });
      }
      if (!data.posBranchId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["posBranchId"],
        });
      }
    }
  });

export type BranchPos = z.input<typeof BranchPosSchema>;

export type AllBranchSettingsType = AdminSetUpType &
  AdminBoostInput &
  BranchPos & {
    branchReceiptBillNumberMedia?: MediaItem | null;
    branchReceiptBillNumberMediaItemId?: string | null;
    branchReceiptOrderTotalMedia?: MediaItem | null;
    branchReceiptOrderTotalMediaItemId?: string | null;
    foodicsAuthorizationToken?: string | null;
    isFoodicsAuthorizationTokenPending?: boolean;
  };

export const POS_NAME_LABELS: Record<string, string> = {
  [PosName.REVEL]: "Revel",
  [PosName.MICROS]: "Micros",
  [PosName.TEVALIS]: "Tevalis",
  [PosName.FOODICS]: "Foodics",
  [PosName.UNKNOWN]: "Unknown",
};

export const POS_WITHOUT_RAW_TYPE = [
  PosName.REVEL,
  PosName.MICROS,
  PosName.TEVALIS,
  PosName.FOODICS,
];
